"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { FESTIVALS } from "@/lib/festival-weather/festivals";
import { GENRE_LABEL, REGION_LABEL, REGION_ORDER } from "@/lib/festival-weather/calendar";
import { festivalDateLabel, festivalListings, upcomingFestivalListings, type FestivalListing } from "@/lib/festival-weather/listings";
import { setShallowSearchParams, useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import type { Region } from "@/lib/festival-weather/types";

const PAGE_SIZE = 4;
const fieldClass = "min-h-11 min-w-0 rounded-lg border border-border bg-white px-3 text-base text-navy";
const actionClass = "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold text-teal-dark hover:bg-teal/6";

function validMonth(value: string | null): value is string {
  return value !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(value) && Number(value.slice(0, 4)) >= 2000 && Number(value.slice(0, 4)) <= 2100;
}

function monthLabel(date: string, long = false): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-GB", {
    month: long ? "long" : "short", year: "numeric", timeZone: "UTC",
  });
}

function dateSummary(listing: FestivalListing) {
  if (listing.confirmed) {
    return listing.windows.map((window) => `${window.label ? `${window.label}: ` : ""}${festivalDateLabel(window.start, window.end)}`).join(" · ");
  }
  const { start, end } = listing.windows[0];
  return `Usually ${monthLabel(start)}${start.slice(0, 7) !== end.slice(0, 7) ? ` to ${monthLabel(end)}` : ""}`;
}

function FestivalRow({ listing, today }: { listing: FestivalListing; today: string }) {
  const { festival, windows, confirmed } = listing;
  const { start, end } = windows[0];
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  const dayLabel = sameMonth
    ? `${Number(start.slice(8))}${start !== end ? `–${Number(end.slice(8))}` : ""}`
    : new Date(`${start}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const happening = confirmed && windows.some((window) => window.start <= today && window.end >= today);

  return (
    <li className="grid grid-cols-[66px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b border-border px-3 py-4 last:border-b-0 sm:grid-cols-[100px_minmax(0,1fr)_auto] sm:gap-x-5 sm:px-5 sm:py-5">
      <div className="self-start border-r border-border pr-2 pt-0.5 sm:self-center sm:pr-4">
        <span className="block text-[15px] font-bold leading-snug text-navy sm:text-lg">
          {confirmed ? dayLabel : new Date(`${start}T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })}
        </span>
        <span className="mt-1 block text-[11px] leading-relaxed text-text-muted">
          {confirmed && sameMonth ? monthLabel(start) : start.slice(0, 4)}
        </span>
      </div>
      <div className="min-w-0">
        <h3 className="font-heading text-base font-bold leading-snug text-navy sm:text-lg">{festival.name}</h3>
        <p className="mt-1 text-xs leading-relaxed text-text-body sm:text-[13px]">
          {festival.city}, {festival.country}
          {festival.genre?.length ? ` · ${festival.genre.map((genre) => GENRE_LABEL[genre]).join(" · ")}` : ""}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-text-body">{dateSummary(listing)}</p>
        <p className={`mt-1 text-xs ${happening ? "font-semibold text-teal-dark" : "text-text-muted"}`}>
          {happening ? "Happening now" : confirmed ? "Confirmed dates" : "Dates TBC"}
        </p>
      </div>
      <Link href={`/festivals/${festival.slug}`} aria-label={`View festival: ${festival.name}`} className="col-start-2 inline-flex min-h-11 items-center gap-2 justify-self-start text-sm font-semibold text-teal-dark hover:underline sm:col-start-3">
        View festival <ArrowRight aria-hidden="true" className="size-4 shrink-0" />
      </Link>
    </li>
  );
}

function MonthTimeline({ listings, month }: { listings: FestivalListing[]; month: string }) {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5));
  const days = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const start = `${month}-01`;
  const end = `${month}-${days}`;

  return (
    <div className="hidden px-5 pb-2 pt-4 md:block">
      <div className="mb-3 grid grid-cols-[220px_minmax(0,1fr)] gap-5 text-xs text-text-muted" aria-hidden="true">
        <span>Solid: confirmed · Dashed: typical</span>
        <div className="grid" style={{ gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))` }}>
          {[1, 8, 15, 22, days].map((day) => <span key={day} className={day === 1 ? "justify-self-start" : day === days ? "justify-self-end" : "justify-self-center"} style={{ gridColumn: day }}>{day}</span>)}
        </div>
      </div>
      <ol aria-label="Festival month calendar" className="divide-y divide-border">
        {listings.map((listing) => (
          <li key={`${listing.festival.slug}-${listing.year}`} className="grid grid-cols-[220px_minmax(0,1fr)] items-center gap-5 py-4">
            <Link href={`/festivals/${listing.festival.slug}`} className="flex min-h-11 flex-col justify-center hover:text-teal-dark">
              <span className="text-sm font-bold">{listing.festival.name}</span>
              <span className="mt-1 text-xs leading-relaxed text-text-body">{dateSummary(listing)}</span>
              {!listing.confirmed && <span className="mt-1 text-xs text-text-muted">Dates TBC</span>}
            </Link>
            <div className="grid min-h-9 items-center rounded-md bg-warm-bg" style={{ gridTemplateColumns: `repeat(${days}, minmax(0, 1fr))` }} aria-hidden="true">
              {listing.windows.map((window) => {
                const first = window.start < start ? 1 : Number(window.start.slice(8));
                const last = window.end > end ? days : Number(window.end.slice(8));
                return <span key={window.start} className={`row-start-1 h-5 rounded ${listing.confirmed ? "bg-teal" : "border border-dashed border-teal bg-teal/10"}`} style={{ gridColumn: `${first} / ${last + 1}` }} />;
              })}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function FestivalBrowser({ initialToday }: { initialToday: string }) {
  const [today, setToday] = useState(initialToday);
  const viewParam = useShallowSearchParam("view");
  const monthParam = useShallowSearchParam("month");
  const regionParam = useShallowSearchParam("region");
  const query = useShallowSearchParam("q") ?? "";
  const view = viewParam === "month" || (viewParam !== "upcoming" && validMonth(monthParam)) ? "month" : "upcoming";
  const month = validMonth(monthParam) ? monthParam : today.slice(0, 7);
  const region = REGION_ORDER.includes(regionParam as Region) ? regionParam as Region : "all";
  const [expanded, setExpanded] = useState({ key: "", count: PAGE_SIZE });
  const filterKey = `${today}:${view}:${month}:${region}:${query}`;
  const visibleCount = expanded.key === filterKey ? expanded.count : PAGE_SIZE;

  useEffect(() => {
    // Preserve the server snapshot for hydration, then refresh cached dates and open tabs.
    const refresh = () => setToday(new Date().toISOString().slice(0, 10));
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const listings = useMemo(() => {
    if (view === "upcoming") return upcomingFestivalListings(FESTIVALS, today);
    const lastDay = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 0)).getUTCDate();
    return festivalListings(FESTIVALS, `${month}-01`, `${month}-${lastDay}`);
  }, [today, view, month]);
  const filtered = listings.filter(({ festival }) =>
    (region === "all" || festival.region === region)
    && `${festival.name} ${festival.city} ${festival.country}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const shown = filtered.slice(0, visibleCount);
  const fullCalendarParams = new URLSearchParams();
  if (region !== "all") fullCalendarParams.set("region", region);
  if (query) fullCalendarParams.set("q", query);
  if (view === "month") fullCalendarParams.set("month", month);
  const fullCalendarHref = `/festivals/calendar${fullCalendarParams.size ? `?${fullCalendarParams}` : ""}`;

  function changeMonth(delta: number) {
    const next = new Date(`${month}-01T00:00:00Z`);
    next.setUTCMonth(next.getUTCMonth() + delta);
    setShallowSearchParams({ view: "month", month: next.toISOString().slice(0, 7) });
  }

  return (
    <section aria-labelledby="festival-calendar-heading">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 md:mb-4">
        <h2 id="festival-calendar-heading" className="font-heading text-[23px] font-bold tracking-tight md:text-[27px]">Festival calendar</h2>
        <Link href={fullCalendarHref} className="inline-flex min-h-11 items-center gap-2 text-[13px] font-semibold text-teal-dark hover:underline">
          Open full calendar <ArrowRight aria-hidden="true" className="size-4" />
        </Link>
      </div>
      <div className="overflow-hidden rounded-[14px] border border-border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-3 sm:px-5 sm:py-4">
          <div role="group" aria-label="Festival view" className="flex gap-1">
            {([['upcoming', 'Upcoming'], ['month', 'Month view']] as const).map(([value, label]) => (
              <button key={value} type="button" aria-pressed={view === value} onClick={() => setShallowSearchParams({ view: value === "upcoming" ? null : value, month: value === "upcoming" ? null : month })} className={`min-h-11 rounded-lg px-3 text-sm font-semibold sm:px-4 ${view === value ? "bg-teal/10 text-teal-dark" : "text-text-body hover:bg-warm-bg"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <label className="relative min-w-0 flex-1 sm:w-[190px]">
              <span className="sr-only">Search festivals</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-3.5 size-4 text-text-muted" />
              <input type="search" placeholder="Search" value={query} onChange={(event) => setShallowSearchParams({ q: event.target.value || null })} className={`${fieldClass} w-full pl-9`} />
            </label>
            <label className="min-w-0 flex-1 sm:flex-none">
              <span className="sr-only">Festival region</span>
              <select value={region} onChange={(event) => setShallowSearchParams({ region: event.target.value === "all" ? null : event.target.value })} className={`${fieldClass} w-full sm:max-w-[185px]`}>
                <option value="all">All regions</option>
                {REGION_ORDER.map((key) => <option key={key} value={key}>{REGION_LABEL[key]}</option>)}
              </select>
            </label>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-3 py-3 text-xs sm:px-5">
          {view === "upcoming" ? <p className="font-semibold">Upcoming festivals <span className="font-normal text-text-muted">· Next 12 months</span></p> : (
            <div className="flex min-w-0 items-center gap-1">
              <button type="button" aria-label="Previous festival month" onClick={() => changeMonth(-1)} className="flex size-11 shrink-0 items-center justify-center rounded-lg hover:bg-warm-bg"><ChevronLeft aria-hidden="true" className="size-4" /></button>
              <label className="min-w-0"><span className="sr-only">Festival month</span><input type="month" value={month} onChange={(event) => { if (validMonth(event.target.value)) setShallowSearchParams({ month: event.target.value }); }} className={`${fieldClass} w-[175px] max-w-full text-sm`} /></label>
              <button type="button" aria-label="Next festival month" onClick={() => changeMonth(1)} className="flex size-11 shrink-0 items-center justify-center rounded-lg hover:bg-warm-bg"><ChevronRight aria-hidden="true" className="size-4" /></button>
            </div>
          )}
          <p role="status" className="text-text-muted">{filtered.length ? `Showing ${shown.length} of ${filtered.length}` : "No matches"}</p>
        </div>
        {shown.length ? (
          <>
            {view === "month" && <MonthTimeline listings={shown} month={month} />}
            <ol aria-label="Festival listings" className={view === "month" ? "md:hidden" : undefined}>
              {shown.map((listing) => <FestivalRow key={`${listing.festival.slug}-${listing.year}`} listing={listing} today={today} />)}
            </ol>
            {filtered.length > shown.length && <div className="border-t border-border px-3 py-2 text-center"><button type="button" className={actionClass} onClick={() => setExpanded({ key: filterKey, count: visibleCount + PAGE_SIZE })}>Show more festivals</button></div>}
          </>
        ) : (
          <div className="px-5 py-7">
            <h3 className="font-heading font-bold">{query || region !== "all" ? "No festivals match these filters." : "No festivals listed for this month."}</h3>
            <p className="mt-2 text-sm text-text-body">{query || region !== "all" ? "Try a different name or region, or clear your filters." : "Try another month or see what’s coming up."}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(query || region !== "all") && <button type="button" className={actionClass} onClick={() => setShallowSearchParams({ q: null, region: null })}>Clear filters</button>}
              {view === "month" && <><button type="button" className={actionClass} onClick={() => changeMonth(1)}>Next month</button><button type="button" className={actionClass} onClick={() => setShallowSearchParams({ view: null, month: null })}>See upcoming festivals</button></>}
            </div>
          </div>
        )}
      </div>
      <p className="mt-3 text-xs leading-relaxed text-text-muted">Dates TBC means the usual festival season is shown. Check the organiser’s confirmed dates before making plans.</p>
    </section>
  );
}
